import { CheckIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { animations, viewportConfig } from '../../config/animations';

const Comparison = () => {
  const { t } = useTranslation();

  const comparisonData = [
    {
      feature: t('comparison.features.weight'),
      hass: t('comparison.hass.weight'),
      comum: t('comparison.common.weight'),
      advantage: 'hass',
    },
    {
      feature: t('comparison.features.skin'),
      hass: t('comparison.hass.skin'),
      comum: t('comparison.common.skin'),
      advantage: 'hass',
    },
    {
      feature: t('comparison.features.pulp'),
      hass: t('comparison.hass.pulp'),
      comum: t('comparison.common.pulp'),
      advantage: 'hass',
    },
    {
      feature: t('comparison.features.flavor'),
      hass: t('comparison.hass.flavor'),
      comum: t('comparison.common.flavor'),
      advantage: 'hass',
    },
    {
      feature: t('comparison.features.durability'),
      hass: t('comparison.hass.durability'),
      comum: t('comparison.common.durability'),
      advantage: 'hass',
    },
    {
      feature: t('comparison.features.market'),
      hass: t('comparison.hass.market'),
      comum: t('comparison.common.market'),
      advantage: 'hass',
    },
  ];


  return (
    <section className='py-8 bg-gradient-to-br from-avoprime-light/10 to-white'>
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <motion.div
          {...animations.fadeInUp}
          whileInView={animations.fadeInUp.animate}
          viewport={viewportConfig}
          className='text-center mb-16'
        >
          <h2 className='text-4xl sm:text-5xl lg:text-6xl font-eras font-bold text-avoprime-primary mb-6'>
            {t('comparison.title')}
          </h2>
          <p className='text-xl lg:text-2xl font-montserrat text-gray-700 max-w-4xl mx-auto leading-relaxed'>
            {t('comparison.subtitle')}
          </p>
        </motion.div>

        {/* Comparison Table - Desktop */}
        <motion.div
          variants={animations.containerVariants}
          initial='hidden'
          whileInView='visible'
          viewport={viewportConfig}
          className='hidden lg:block bg-white rounded-3xl shadow-2xl overflow-hidden mb-16'
        >
          {/* Header */}
          <div className='bg-gradient-to-r from-avoprime-primary to-avoprime-secondary text-white p-6 lg:p-8'>
            <div className='grid grid-cols-3 gap-4 lg:gap-8'>
              <div className='text-center'>
                <h3 className='text-xl lg:text-2xl font-eras font-bold'>
                  {t('comparison.table.characteristic')}
                </h3>
              </div>
              <div className='text-center'>
                <h3 className='text-xl lg:text-2xl font-eras font-bold'>
                  {t('comparison.table.hass')}
                </h3>
                <p className='text-sm opacity-90 mt-1'>
                  {t('comparison.table.premium')}
                </p>
              </div>
              <div className='text-center'>
                <h3 className='text-xl lg:text-2xl font-eras font-bold'>
                  {t('comparison.table.common')}
                </h3>
                <p className='text-sm opacity-90 mt-1'>
                  {t('comparison.table.traditional')}
                </p>
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className='divide-y divide-gray-100'>
            {comparisonData.map((row, index) => (
              <motion.div
                key={index}
                variants={animations.itemVariantsHorizontal}
                className='grid grid-cols-3 gap-4 lg:gap-8 p-6 lg:p-8 hover:bg-gray-50 transition-colors duration-200'
              >
                <div className='flex items-center justify-center'>
                  <span className='font-eras font-bold text-lg text-avoprime-primary text-left'>
                    {row.feature}
                  </span>
                </div>
                <div className='text-center'>
                  <div className='flex items-center justify-center space-x-2'>
                    <span className='font-montserrat text-lg'>{row.hass}</span>
                    {row.advantage === 'hass' && (
                      <CheckIcon className='w-6 h-6 text-green-500' />
                    )}
                  </div>
                </div>
                <div className='text-center'>
                  <div className='flex items-center justify-center space-x-2'>
                    <span className='font-montserrat text-lg'>{row.comum}</span>
                    {row.advantage === 'comum' && (
                      <CheckIcon className='w-6 h-6 text-green-500' />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Comparison Cards - Mobile */}
        <motion.div
          variants={animations.containerVariants}
          initial='hidden'
          whileInView='visible'
          viewport={viewportConfig}
          className='lg:hidden space-y-4 mb-16'
        >
          {comparisonData.map((row, index) => (
            <motion.div
              key={index}
              variants={animations.itemVariants}
              className='bg-white rounded-2xl shadow-lg p-6 border border-gray-100'
            >
              {/* Feature Title */}
              <h4 className='font-eras font-bold text-lg text-avoprime-primary mb-4 text-center'>
                {row.feature}
              </h4>
              
              {/* Comparison Cards */}
              <div className='grid grid-cols-2 gap-4'>
                {/* Hass Card */}
                <div className={`p-4 rounded-xl border-2 ${
                  row.advantage === 'hass' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className='text-center'>
                    <h5 className='font-eras font-bold text-sm text-avoprime-primary mb-2'>
                      {t('comparison.table.hass')}
                    </h5>
                    <p className='text-xs text-gray-600 mb-2'>
                      {t('comparison.table.premium')}
                    </p>
                    <div className='flex items-center justify-center space-x-2'>
                      <span className='font-montserrat text-sm'>{row.hass}</span>
                      {row.advantage === 'hass' && (
                        <CheckIcon className='w-5 h-5 text-green-500' />
                      )}
                    </div>
                  </div>
                </div>

                {/* Common Card */}
                <div className={`p-4 rounded-xl border-2 ${
                  row.advantage === 'comum' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}>
                  <div className='text-center'>
                    <h5 className='font-eras font-bold text-sm text-avoprime-primary mb-2'>
                      {t('comparison.table.common')}
                    </h5>
                    <p className='text-xs text-gray-600 mb-2'>
                      {t('comparison.table.traditional')}
                    </p>
                    <div className='flex items-center justify-center space-x-2'>
                      <span className='font-montserrat text-sm'>{row.comum}</span>
                      {row.advantage === 'comum' && (
                        <CheckIcon className='w-5 h-5 text-green-500' />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default Comparison;
